import { onMounted, onBeforeUnmount } from 'vue'
import { eventBus } from '../eventBus'
import { log } from '../utils/DebugLogger'

export interface ShortcutDefinition {
  key: string
  modifiers: string[]
  action: string | (() => void)
  description: string
}

export function useKeyboardShortcuts() {
  const isMac = (): boolean => {
    return navigator.platform.toUpperCase().indexOf('MAC') >= 0 || navigator.userAgent.toUpperCase().indexOf('MAC') >= 0
  }

  const hasModifier = (event: KeyboardEvent): boolean => {
    return isMac() ? event.metaKey : event.ctrlKey
  }

  const shortcuts: ShortcutDefinition[] = [
    {
      key: '.',
      modifiers: ['ctrl/cmd'],
      // Aponta para os chamados da plataforma, nao para o modal do Odoo
      // (decisao do usuario, 2026-09-02 — ver FloatingTicketButton.vue).
      action: 'modal.chamados.open',
      description: 'Abrir o modal de novo chamado'
    },
    {
      key: 'b',
      modifiers: ['ctrl/cmd'],
      action: 'modal.global-search.open',
      description: 'Open the system-wide search modal'
    }
  ]

  const handleKeydown = (event: KeyboardEvent): void => {
    const activeElement = document.activeElement as HTMLElement | null
    const isTyping =
      activeElement &&
      (activeElement.tagName === 'INPUT' ||
        activeElement.tagName === 'TEXTAREA' ||
        (activeElement as any).isContentEditable ||
        activeElement.closest('.p-editor-content'))

    for (const shortcut of shortcuts) {
      if (matchesShortcut(event, shortcut)) {
        log('useKeyboardShortcuts', 'handleKeydown', 'Shortcut matched', {
          key: shortcut.key,
          description: shortcut.description,
          isTyping
        })

        event.preventDefault()
        event.stopPropagation()

        if (typeof shortcut.action === 'function') {
          shortcut.action()
        } else if (typeof shortcut.action === 'string') {
          eventBus.emit(shortcut.action)
          log('useKeyboardShortcuts', 'handleKeydown', 'Event emitted', shortcut.action)
        }

        break
      }
    }
  }

  const matchesShortcut = (event: KeyboardEvent, shortcut: ShortcutDefinition): boolean => {
    if (event.key.toLowerCase() !== shortcut.key.toLowerCase()) {
      return false
    }

    for (const modifier of shortcut.modifiers) {
      if (modifier === 'ctrl/cmd') {
        if (!hasModifier(event)) return false
      } else if (modifier === 'shift') {
        if (!event.shiftKey) return false
      } else if (modifier === 'alt') {
        if (!event.altKey) return false
      }
    }

    const wantsCtrlCmd = shortcut.modifiers.includes('ctrl/cmd')
    const wantsShift = shortcut.modifiers.includes('shift')
    const wantsAlt = shortcut.modifiers.includes('alt')

    if (!wantsCtrlCmd && hasModifier(event)) return false
    if (!wantsShift && event.shiftKey) return false
    if (!wantsAlt && event.altKey) return false

    return true
  }

  const registerShortcut = (shortcutDef: ShortcutDefinition): void => {
    shortcuts.push(shortcutDef)
    log('useKeyboardShortcuts', 'registerShortcut', 'Shortcut registered', shortcutDef)
  }

  const unregisterShortcut = (key: string, modifiers: string[]): void => {
    const index = shortcuts.findIndex(
      (s) => s.key.toLowerCase() === key.toLowerCase() && JSON.stringify(s.modifiers.sort()) === JSON.stringify(modifiers.sort())
    )
    if (index !== -1) {
      shortcuts.splice(index, 1)
      log('useKeyboardShortcuts', 'unregisterShortcut', 'Shortcut unregistered', { key, modifiers })
    }
  }

  const getModifierLabel = (): string => {
    return isMac() ? '⌘' : 'Ctrl'
  }

  const getShortcutLabel = (shortcutDef: ShortcutDefinition): string => {
    const parts: string[] = []

    for (const modifier of shortcutDef.modifiers) {
      if (modifier === 'ctrl/cmd') {
        parts.push(getModifierLabel())
      } else if (modifier === 'shift') {
        parts.push(isMac() ? '⇧' : 'Shift')
      } else if (modifier === 'alt') {
        parts.push(isMac() ? '⌥' : 'Alt')
      }
    }

    parts.push(shortcutDef.key.toUpperCase())
    return parts.join(isMac() ? '' : '+')
  }

  const initGlobalShortcuts = (): void => {
    log('useKeyboardShortcuts', 'initGlobalShortcuts', 'Initializing keyboard shortcuts')
    document.addEventListener('keydown', handleKeydown, true)
  }

  const cleanupGlobalShortcuts = (): void => {
    log('useKeyboardShortcuts', 'cleanupGlobalShortcuts', 'Cleaning up keyboard shortcuts')
    document.removeEventListener('keydown', handleKeydown, true)
  }

  return {
    isMac,
    hasModifier,
    shortcuts,
    registerShortcut,
    unregisterShortcut,
    getModifierLabel,
    getShortcutLabel,
    initGlobalShortcuts,
    cleanupGlobalShortcuts
  }
}

export function useKeyboardShortcutsWithLifecycle() {
  const shortcuts = useKeyboardShortcuts()

  onMounted(() => {
    shortcuts.initGlobalShortcuts()
  })

  onBeforeUnmount(() => {
    shortcuts.cleanupGlobalShortcuts()
  })

  return shortcuts
}
